import { useMemo, useState } from 'react'
import { Link, Outlet, useParams, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useUser } from '@/hooks/use-user'
import { getUserId } from '@/api/generated/user/user'
import { Button } from '@/components/ui/button'
import { ComposeMessageDialog } from '@/features/messages/ComposeMessageDialog'

// Pill styling shared with league-secondary-nav / team-secondary-nav so the
// user-area rail reads identically to the rest of the site.
const pillBase =
  'inline-flex items-center px-3 py-1.5 text-sm rounded transition-colors whitespace-nowrap'
const pillInactive = 'text-slate-900 hover:bg-slate-100'
const pillActive = 'bg-navy text-white'

export function UserLayout() {
  const { userId } = useParams()
  const { user: currentUser } = useUser()
  const location = useLocation()

  const numericUserId = userId ? Number(userId) : null
  const isOwnProfile = currentUser !== null && numericUserId === currentUser.id
  const [composeOpen, setComposeOpen] = useState(false)

  const basePath = `/user/${userId}`
  const onDashboard = location.pathname === basePath

  // Resolve the viewed user's display name for the identity heading. Own profile
  // comes straight from the auth context; for anyone else we run the same
  // GET /User/{id} query the Records/Timeline pages already use (identical query
  // key, so TanStack Query dedupes — no extra request). This is what lets the
  // band name another user's Dashboard, which has no heading of its own.
  const { data: viewedUser } = useQuery({
    queryKey: ['user', numericUserId, 'profile'],
    queryFn: () => getUserId(numericUserId!),
    enabled: numericUserId !== null && numericUserId > 0 && !isOwnProfile,
  })

  const displayName = useMemo(() => {
    if (isOwnProfile && currentUser) return currentUser.username
    if (viewedUser?.userName) return viewedUser.userName
    return numericUserId ? `User ${numericUserId}` : ''
  }, [isOwnProfile, currentUser, viewedUser, numericUserId])

  // This rail's job is browsing a user's *public* pages, and it works for any
  // user (you can view another manager's Dashboard/Records/Timeline here — the
  // Profile menu only ever points at your own). Settings and Team Requests are
  // own-only account items, so they live solely in the Profile menu (Header.tsx),
  // not here.
  const navItems = [
    { label: 'Dashboard', to: basePath, active: onDashboard },
    { label: 'Records', to: `${basePath}/records`, active: location.pathname.endsWith('/records') },
    { label: 'Timeline', to: `${basePath}/timeline`, active: location.pathname.endsWith('/timeline') },
  ]

  // Muted " - PageTitle" suffix on the identity heading, mirroring the
  // league/team nav bands' pageTitle. Derived from the path (the page renders
  // via <Outlet/>, so it can't pass a prop up here) for the sub-pages that
  // carry their title in the band instead of an in-body h1. Dashboard is the
  // identity home (no suffix); Settings keeps its own polished treatment.
  const pageTitle = useMemo(() => {
    const p = location.pathname
    if (p.endsWith('/records')) return 'Records'
    if (p.endsWith('/timeline')) return 'Timeline'
    if (p.endsWith('/teamrequests')) return 'Team Requests'
    return null
  }, [location.pathname])

  return (
    <div>
      {/* Nav band mirrors league/team secondary nav: navy-pill rail on top
          with a bottom-border separator, identity title below. Negative
          margins escape the Layout shell padding (px-4 py-4 sm:py-6) so the
          band extends edge-to-edge — same trick as the other nav rails. */}
      <nav
        aria-label="User navigation"
        className="border-b border-slate-200 bg-white -mx-1 sm:-mx-4 -mt-4 sm:-mt-6"
      >
        <div className="mx-auto w-full max-w-[1180px] px-4 pt-2 pb-3">
          <div className="relative pb-2 mb-3 border-b border-slate-200">
            <ul className="flex items-center gap-1 flex-nowrap overflow-x-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]">
              {navItems.map(item => (
                <li key={item.to}>
                  <Link
                    to={item.to}
                    aria-current={item.active ? 'page' : undefined}
                    className={[pillBase, item.active ? pillActive : pillInactive].join(' ')}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Identity title: the viewed user's username (own or anyone else's),
              mirroring how the league/team name fills the title slot. Links back
              to that user's dashboard from sub-pages; renders plain on the
              dashboard itself (the destination), per the
              hide-identity-link-on-destination rule. */}
          {displayName && (
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-xl font-heading font-bold text-navy truncate">
                {onDashboard ? (
                  displayName
                ) : (
                  <Link
                    to={basePath}
                    className="underline decoration-dotted decoration-1 decoration-blue underline-offset-4 hover:decoration-solid hover:decoration-navy"
                  >
                    {displayName}
                  </Link>
                )}
                {pageTitle && <span className="font-normal text-slate-500"> - {pageTitle}</span>}
              </h2>
              {!isOwnProfile && currentUser && numericUserId != null && (
                <>
                  <Button variant="outline" size="sm" onClick={() => setComposeOpen(true)}>
                    Message
                  </Button>
                  <ComposeMessageDialog
                    recipientUserId={numericUserId}
                    recipientUsername={displayName}
                    open={composeOpen}
                    onOpenChange={setComposeOpen}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </nav>

      <Outlet />
    </div>
  )
}
